/* eslint-disable react/prop-types */ // TODO: add props
import { useState } from 'react';
import { Button } from 'edrolo-style-guide';
import WistiaUtils from 'edrolo/utils/WistiaUtils';
import EquationEditorInline from 'edrolo/components/exams/complex_response/EquationEditorInline';
import { KEYPAD_LAYOUT } from 'edrolo/components/exams/complex_response/editor/layouts/KeypadLayoutFactory';
import EquationSolution from 'edrolo/components/exams/solutions/EquationSolution';
import QuestionSolution from '../../../QuestionSolution';
import GrokSelfReflection from '../../../GrokSelfReflection';
import styles from './SimpleMathQuestion.scss';
import { EXAM_QUESTION_TYPES } from 'edrolo/constants/QuestionTypes';
import questionTypePropInterface from '../../questionTypePropInterface';
import SectionHeading from '../../../SectionHeading';

const getSingleField = (question) => question.getIn(['fields', 0]);

const SimpleMathAnswer = ({ question, isCorrect, value }) => {
  const field = getSingleField(question);
  let displayValue = value;
  // If no value is passed in, look up the correct value from the question.
  // (used for TeacherQuestionView)
  if (typeof value === 'undefined') {
    const correctOptionValue = question
      .get('options')
      .find((option) => field.get('correct_options').includes(option.get('id')))
      ?.get('value', '');
    displayValue = correctOptionValue;
  }
  const prefix = field.get('prefix');
  const suffix = field.get('suffix');

  return (
    <>
      <SectionHeading>Your response</SectionHeading>
      <div
        className={styles.answer}
        data-test={`answer-${EXAM_QUESTION_TYPES.SIMPLE_MATH}`}
      >
        {prefix && <span>{prefix}</span>}
        <EquationSolution equations={[displayValue]} />
        {suffix && <span>{suffix}</span>}

        {/* We're disabling this to limit complaints about students correct answers being marked */}
        {/* <span className={cx(styles.check, { [styles.incorrect]: !isCorrect })} /> */}
      </div>
    </>
  );
};

const SimpleMathQuestion = ({
  question,
  response,
  onSubmit,
  onSubmitGrok,
  grokkingItemIsSaving,
  onSubmitTeacherAnswer,
}) => {
  const field = getSingleField(question);
  const [isActive, setIsActive] = useState(false);
  const existingEquation =
    response?.getIn(['field_responses', 0, 'submitted_value'], '') || '';
  const [updatedEquation, setUpdatedEquation] = useState(existingEquation);
  const hasUnsubmittedResponse = updatedEquation !== existingEquation;
  const keyboard = KEYPAD_LAYOUT.SIMPLE_MATHS;
  const NUMBER_OF_LINES = 1;
  const isCorrect = response?.get('is_correct', false);
  const prefix = field.get('prefix');
  const suffix = field.get('suffix');
  const fieldId = field.get('id');

  const handleSubmitResponse = () => {
    const fieldResponse = {
      fieldId: fieldId,
      submittedValue: updatedEquation,
    };
    onSubmit({ fieldResponses: [fieldResponse] });
  };

  if (!response) {
    return (
      // Using 'simple-math-question' class to override
      // equationEditorInline styles in _complex-response.scss

      <div
        className={`simple-math-question ${styles.simpleMathQuestion}`}
        data-test={`question-${EXAM_QUESTION_TYPES.SIMPLE_MATH}`}
      >
        <div className={styles.question}>
          {prefix && <span>{prefix}</span>}
          <EquationEditorInline
            isActive={isActive}
            numberOfLines={NUMBER_OF_LINES}
            equations={[updatedEquation]}
            keypadLayout={keyboard}
            onClick={() => setIsActive(true)}
            onSave={({ equations: [newEquation] }) => {
              setUpdatedEquation(newEquation);
            }}
            onClose={() => {
              setIsActive(false);
            }}
            saving={false}
            allowAddingLines={false}
            showNavWhenActive={false}
            debounceAutoSave={false}
          />
          {suffix && <span>{suffix}</span>}
        </div>

        <Button
          onClick={() =>
            onSubmitTeacherAnswer
              ? onSubmitTeacherAnswer()
              : handleSubmitResponse()
          }
          disabled={!hasUnsubmittedResponse}
          data-test={`submit-${EXAM_QUESTION_TYPES.SIMPLE_MATH}`}
        >
          {onSubmitTeacherAnswer ? 'View answer' : 'Save & continue'}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <QuestionSolution
        headingText="Solution"
        questionId={question.get('id')}
        explanation={response.getIn(['solution', 'explanation'])}
        videoHash={response.getIn(['solution', 'video', 'wistia_hashed_id'])}
        videoThumbnailUrl={WistiaUtils.getThumbnail(
          response.getIn(['solution', 'video'])
        )}
      />
      <SimpleMathAnswer
        question={question}
        isCorrect={isCorrect}
        value={existingEquation}
      />
      <GrokSelfReflection
        value={response.get('grokked')}
        onChange={onSubmitGrok}
        isSaving={grokkingItemIsSaving}
        className={styles.selfReflection}
      />
    </div>
  );
};

SimpleMathQuestion.propTypes = {
  ...questionTypePropInterface,
};

export { SimpleMathQuestion, SimpleMathAnswer };
